/**
 * Cart service for Shopify operations
 * Handles all cart-related API calls
 */

import { BaseShopifyService } from "../services/base";
import {
  CREATE_CART,
  GET_CART,
  ADD_CART_LINES,
  REMOVE_CART_LINES,
  UPDATE_CART_LINES,
} from "./queries";
import {
  mapAddToCartToResponse,
  mapCreateCartToResponse,
  mapGetCartToResponse,
  mapRemoveFromCartToResponse,
  mapUpdateCartToResponse,
} from "./mappers";
import {
  ICartItemRequest,
  ICartUpdateRequest,
  IOptionsRequest,
  IResponse,
} from "../../../models";
import { formatResponse } from "../../../core/utils";

export class CartService extends BaseShopifyService {
  /**
   * Create a new cart
   */
  async createCart(options?: IOptionsRequest): Promise<IResponse> {
    try {
      const query = options?.query || CREATE_CART;
      const variables = options?.variables || { input: {} };

      // ✅ Execute GraphQL query
      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in CREATE_CART", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to create cart due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (
        !result?.data ||
        !result.data.cartCreate ||
        !result.data.cartCreate.cart
      ) {
        this.logger.error(
          "Invalid response structure in CREATE_CART",
          undefined,
          {
            result: result?.data,
          }
        );

        return formatResponse({
          success: false,
          message: "Failed to create cart due to invalid response structure.",
          error: {
            message: "Invalid response from Shopify API.",
            details: result?.data,
          },
        });
      }

      // ✅ Check for Shopify userErrors in cartCreate
      if (
        result.data.cartCreate.userErrors &&
        result.data.cartCreate.userErrors.length > 0
      ) {
        this.logger.error("Shopify userErrors in CREATE_CART", undefined, {
          userErrors: result.data.cartCreate.userErrors,
        });

        const errorMessages = result.data.cartCreate.userErrors
          .map((error: { field: string; message: string }) => error.message)
          .join(", ");

        return formatResponse({
          success: false,
          message: "Failed to create cart due to validation errors.",
          error: {
            message: errorMessages,
            details: result.data.cartCreate.userErrors,
          },
        });
      }

      // ✅ Normal success path
      return formatResponse({
        success: true,
        message: "Cart created successfully.",
        data: mapCreateCartToResponse(result.data.cartCreate.cart),
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in createCart", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while creating the cart.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Get a cart by ID
   */
  async getCart(id: string, options?: IOptionsRequest): Promise<IResponse> {
    try {
      // ✅ Validation & Input Processing
      if (!id) {
        return formatResponse({
          success: false,
          message: "No cart ID provided.",
        });
      }

      const cartId = id.toString().includes("gid://")
        ? id.toString()
        : `gid://shopify/Cart/${id.toString()}`;

      const query = options?.query || GET_CART;
      const variables = options?.variables || { cartId };

      // ✅ Execute GraphQL query
      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_CART", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch cart due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result?.data || !result.data.cart) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. No cart found.",
          data: null,
        });
      }

      // ✅ Normal success path - Use optimized mapper with enhanced metadata

      return formatResponse({
        success: true,
        message: "Cart retrieved successfully.",
        data: mapGetCartToResponse(result.data.cart),
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getCart", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while retrieving the cart.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Update a cart
   */
  async updateCart(
    id: string,
    data: ICartUpdateRequest,
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // ✅ Validation & Input Processing
      if (!id) {
        return formatResponse({
          success: false,
          message: "No cart ID provided.",
        });
      }

      if (!data.items || data.items.length === 0) {
        // If no items provided, just get the cart
        return this.getCart(id, options);
      }

      const cartId = id.toString().includes("gid://")
        ? id.toString()
        : `gid://shopify/Cart/${id.toString()}`;

      // Get the current cart to get the line IDs
      const currentCartResponse = await this.getCart(id, options);
      if (!currentCartResponse.success || !currentCartResponse.data) {
        return currentCartResponse; // Return the error response from getCart
      }

      const currentCart = currentCartResponse.data;

      // Map the items to the format expected by Shopify
      const lines = data.items
        .map((item) => ({
          id: currentCart.lines?.find(
            (cartItem: any) =>
              cartItem.merchandise?.product?.id?.split("/")?.pop() ===
                item.productId && cartItem.merchandise?.id === item.variantId
          )?.id,
          quantity: item.quantity,
          merchandiseId: item.variantId,
        }))
        .filter((line) => line.id); // Only include lines that exist in the cart

      const query = options?.query || UPDATE_CART_LINES;
      const variables = options?.variables || {
        cartId,
        lines,
      };

      // ✅ Execute GraphQL query
      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in UPDATE_CART_LINES", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to update cart due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (
        !result?.data ||
        !result.data.cartLinesUpdate ||
        !result.data.cartLinesUpdate.cart
      ) {
        this.logger.error(
          "Invalid response structure in UPDATE_CART_LINES",
          undefined,
          {
            result: result?.data,
          }
        );

        return formatResponse({
          success: false,
          message: "Failed to update cart due to invalid response structure.",
          error: {
            message: "Invalid response from Shopify API.",
            details: result?.data,
          },
        });
      }

      // ✅ Check for Shopify userErrors in cartLinesUpdate
      if (
        result.data.cartLinesUpdate.userErrors &&
        result.data.cartLinesUpdate.userErrors.length > 0
      ) {
        this.logger.error(
          "Shopify userErrors in UPDATE_CART_LINES",
          undefined,
          {
            userErrors: result.data.cartLinesUpdate.userErrors,
          }
        );

        const errorMessages = result.data.cartLinesUpdate.userErrors
          .map((error: { field: string; message: string }) => error.message)
          .join(", ");

        return formatResponse({
          success: false,
          message: "Failed to update cart due to validation errors.",
          error: {
            message: errorMessages,
            details: result.data.cartLinesUpdate.userErrors,
          },
        });
      }

      // ✅ Normal success path - Use optimized mapper with enhanced metadata
      return formatResponse({
        success: true,
        message: "Cart updated successfully.",
        data: mapUpdateCartToResponse(result.data.cartLinesUpdate.cart),
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in updateCart", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while updating the cart.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Add items to a cart
   */
  async addToCart(
    cartId: string,
    items: ICartItemRequest[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // ✅ Validation & Input Processing
      if (!cartId) {
        return formatResponse({
          success: false,
          message: "No cart ID provided.",
        });
      }

      if (!items || items.length === 0) {
        return formatResponse({
          success: false,
          message: "No items provided to add to cart.",
        });
      }

      const formattedCartId = cartId.toString().includes("gid://")
        ? cartId.toString()
        : `gid://shopify/Cart/${cartId.toString()}`;

      // Map the items to the format expected by Shopify
      const lines = items.map((item) => ({
        quantity: item.quantity,
        merchandiseId: item.variantId.toString().includes("gid://")
          ? item.variantId.toString()
          : `gid://shopify/ProductVariant/${item.variantId.toString()}`,
      }));

      const query = options?.query || ADD_CART_LINES;
      const variables = options?.variables || {
        cartId: formattedCartId,
        lines,
      };

      // ✅ Execute GraphQL query
      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in ADD_CART_LINES", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to add items to cart due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (
        !result?.data ||
        !result.data.cartLinesAdd ||
        !result.data.cartLinesAdd.cart
      ) {
        this.logger.error(
          "Invalid response structure in ADD_CART_LINES",
          undefined,
          {
            result: result?.data,
          }
        );

        return formatResponse({
          success: false,
          message:
            "Failed to add items to cart due to invalid response structure.",
          error: {
            message: "Invalid response from Shopify API.",
            details: result?.data,
          },
        });
      }

      // ✅ Check for Shopify userErrors in cartLinesAdd
      if (
        result.data.cartLinesAdd.userErrors &&
        result.data.cartLinesAdd.userErrors.length > 0
      ) {
        this.logger.error("Shopify userErrors in ADD_CART_LINES", undefined, {
          userErrors: result.data.cartLinesAdd.userErrors,
        });

        const errorMessages = result.data.cartLinesAdd.userErrors
          .map((error: { field: string; message: string }) => error.message)
          .join(", ");

        return formatResponse({
          success: false,
          message: "Failed to add items to cart due to validation errors.",
          error: {
            message: errorMessages,
            details: result.data.cartLinesAdd.userErrors,
          },
        });
      }

      // ✅ Normal success path
      return formatResponse({
        success: true,
        message: "Items added to cart successfully.",
        data: mapAddToCartToResponse(result.data.cartLinesAdd.cart),
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in addToCart", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while adding items to the cart.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }

  /**
   * Remove items from a cart
   */
  async removeFromCart(
    cartId: string,
    itemIds: string[],
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // ✅ Validation & Input Processing
      if (!cartId) {
        return formatResponse({
          success: false,
          message: "No cart ID provided.",
        });
      }

      if (!itemIds || itemIds.length === 0) {
        return formatResponse({
          success: false,
          message: "No item IDs provided to remove from cart.",
        });
      }

      const formattedCartId = cartId.toString().includes("gid://")
        ? cartId.toString()
        : `gid://shopify/Cart/${cartId.toString()}`;

      // Format the line IDs if needed
      const lineIds = itemIds.map((id) =>
        id.toString().includes("gid://") ? id.toString() : id.toString()
      );

      const query = options?.query || REMOVE_CART_LINES;
      const variables = options?.variables || {
        cartId: formattedCartId,
        lineIds,
      };

      // ✅ Execute GraphQL query
      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in REMOVE_CART_LINES", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to remove items from cart due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (
        !result?.data ||
        !result.data.cartLinesRemove ||
        !result.data.cartLinesRemove.cart
      ) {
        this.logger.error(
          "Invalid response structure in REMOVE_CART_LINES",
          undefined,
          {
            result: result?.data,
          }
        );

        return formatResponse({
          success: false,
          message:
            "Failed to remove items from cart due to invalid response structure.",
          error: {
            message: "Invalid response from Shopify API.",
            details: result?.data,
          },
        });
      }

      // ✅ Check for Shopify userErrors in cartLinesRemove
      if (
        result.data.cartLinesRemove.userErrors &&
        result.data.cartLinesRemove.userErrors.length > 0
      ) {
        this.logger.error(
          "Shopify userErrors in REMOVE_CART_LINES",
          undefined,
          {
            userErrors: result.data.cartLinesRemove.userErrors,
          }
        );

        const errorMessages = result.data.cartLinesRemove.userErrors
          .map((error: { field: string; message: string }) => error.message)
          .join(", ");

        return formatResponse({
          success: false,
          message: "Failed to remove items from cart due to validation errors.",
          error: {
            message: errorMessages,
            details: result.data.cartLinesRemove.userErrors,
          },
        });
      }

      // ✅ Normal success path
      return formatResponse({
        success: true,
        message: "Items removed from cart successfully.",
        data: mapRemoveFromCartToResponse(result.data.cartLinesRemove.cart),
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in removeFromCart", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while removing items from the cart.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }
}
